import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { getFolderNotes, createFolderNote, updateFolderNote, deleteFolderNote } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import AggregationToggle from './AggregationToggle'
import FolderNoteEditor from './FolderNoteEditor'
import NoteVisibilityBadge from './NoteVisibilityBadge'
import type { FolderNote } from '../../types'

type NoteFilter = 'all' | 'private' | 'shared'

interface FolderNotesListProps {
  projectId: string
  folderId: string
}

export default function FolderNotesList({ projectId, folderId }: FolderNotesListProps) {
  const [aggregate, setAggregate] = useState(false)
  const [filter, setFilter] = useState<NoteFilter>('all')
  const [showEditor, setShowEditor] = useState(false)
  const [editingNote, setEditingNote] = useState<FolderNote | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [confirmVisibility, setConfirmVisibility] = useState<FolderNote | null>(null)
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const currentUserId = user?.id || ''

  const { data: notesData, isLoading } = useQuery({
    queryKey: ['folder-notes', projectId, folderId, aggregate],
    queryFn: () => getFolderNotes(projectId, folderId, aggregate),
  })

  const createMutation = useMutation({
    mutationFn: ({ content, visibility }: { content: string; visibility: 'private' | 'shared' }) =>
      createFolderNote(projectId, folderId, content, visibility),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-notes', projectId, folderId] })
      setShowEditor(false)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ noteId, updates }: { noteId: string; updates: { content?: string; visibility?: 'private' | 'shared' } }) =>
      updateFolderNote(projectId, folderId, noteId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-notes', projectId, folderId] })
      setEditingNote(null)
      setConfirmVisibility(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteFolderNote(projectId, folderId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['folder-notes', projectId, folderId] })
      setConfirmDelete(null)
    },
  })

  const notes = (notesData?.notes || []).filter((n) => {
    if (filter === 'private') return n.visibility === 'private'
    if (filter === 'shared') return n.visibility === 'shared'
    return true
  })

  const handleVisibilityToggle = (note: FolderNote) => {
    if (note.visibility === 'private') {
      // Private → Shared needs confirmation
      setConfirmVisibility(note)
    } else {
      // Shared → Private — no confirmation needed
      updateMutation.mutate({ noteId: note.note_id, updates: { visibility: 'private' } })
    }
  }

  const handleDelete = (note: FolderNote) => {
    if (note.visibility === 'shared') {
      setConfirmDelete(note.note_id)
    } else {
      deleteMutation.mutate(note.note_id)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEditor(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-iflow-700 text-white rounded-lg hover:bg-iflow-800"
          >
            <Plus className="w-3.5 h-3.5" /> Add Note
          </button>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden text-xs">
            {(['all', 'private', 'shared'] as NoteFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-2.5 py-1 capitalize ${filter === f ? 'bg-iflow-700 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {f === 'private' ? 'My Private' : f === 'shared' ? 'Shared Only' : 'All'}
              </button>
            ))}
          </div>
        </div>
        <AggregationToggle projectId={projectId} folderId={folderId} value={aggregate} onChange={setAggregate} />
      </div>

      {showEditor && (
        <FolderNoteEditor
          onSubmit={(content, visibility) => createMutation.mutate({ content, visibility })}
          onCancel={() => setShowEditor(false)}
          isSubmitting={createMutation.isPending}
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-gray-500">No notes yet. Add one above.</p>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => {
            const isAuthor = note.author_id === currentUserId

            if (editingNote?.note_id === note.note_id) {
              return (
                <FolderNoteEditor
                  key={note.note_id}
                  initialContent={note.content}
                  initialVisibility={note.visibility}
                  onSubmit={(content, visibility) =>
                    updateMutation.mutate({ noteId: note.note_id, updates: { content, visibility } })
                  }
                  onCancel={() => setEditingNote(null)}
                  isSubmitting={updateMutation.isPending}
                />
              )
            }

            return (
              <div key={note.note_id} className="p-3 border border-gray-100 rounded-lg">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.content}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
                      <span>{note.author_name}</span>
                      <span>{new Date(note.created_at).toLocaleDateString()}</span>
                      <NoteVisibilityBadge
                        visibility={note.visibility}
                        clickable={isAuthor}
                        onClick={isAuthor ? () => handleVisibilityToggle(note) : undefined}
                      />
                      {note.source_folder_id && (
                        <span className="px-1.5 py-0.5 bg-gray-100 rounded">from child folder</span>
                      )}
                    </div>
                  </div>
                  {isAuthor && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setEditingNote(note)} className="p-1 text-gray-500 hover:text-iflow-500">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(note)} className="p-1 text-gray-500 hover:text-red-500">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Confirm visibility change dialog */}
      {confirmVisibility && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmVisibility(null)} />
          <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <p className="text-sm text-gray-900">This note will become visible to all folder collaborators. Continue?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmVisibility(null)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg">Cancel</button>
              <button
                onClick={() => updateMutation.mutate({ noteId: confirmVisibility.note_id, updates: { visibility: 'shared' } })}
                className="px-3 py-1.5 text-sm bg-iflow-700 text-white rounded-lg"
              >
                Make Shared
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete shared note dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
            <p className="text-sm text-gray-900">This shared note will be permanently deleted. Continue?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg">Cancel</button>
              <button
                onClick={() => deleteMutation.mutate(confirmDelete)}
                className="px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
